import React, { useState, useEffect, useCallback } from "react";

const BASE_API_URL = process.env.REACT_APP_API_URL || "https://api.virtualkalvi.com";
const API_KEY      = process.env.REACT_APP_API_KEY  || "";

const headers = {
  "Content-Type": "application/json",
  "X-API-Key": API_KEY,
};

// ─── Inline styles ────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
    fontFamily: "'Segoe UI', sans-serif",
    color: "#e2e8f0",
    padding: "24px 16px 80px",
  },
  card: {
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 16,
    padding: "20px 22px",
    marginBottom: 20,
    backdropFilter: "blur(8px)",
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    color: "#fff",
    marginBottom: 4,
  },
  sub: {
    fontSize: 13,
    color: "#94a3b8",
    marginBottom: 18,
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#94a3b8",
    marginBottom: 5,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    width: "100%",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 10,
    color: "#fff",
    padding: "10px 14px",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    marginBottom: 14,
  },
  select: {
    width: "100%",
    background: "#1e3a5f",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 10,
    color: "#fff",
    padding: "10px 14px",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    marginBottom: 14,
  },
  row: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 6,
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
  },
  btnGreen: {
    background: "linear-gradient(135deg, #16a34a, #15803d)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
  },
  btnRed: {
    background: "linear-gradient(135deg, #dc2626, #b91c1c)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
  },
  btnGray: {
    background: "rgba(255,255,255,0.12)",
    color: "#e2e8f0",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 10,
    padding: "10px 20px",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  },
  statusDot: (on) => ({
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: on ? "#22c55e" : "#ef4444",
    marginRight: 7,
    boxShadow: on ? "0 0 6px #22c55e" : "none",
  }),
  logRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "9px 0",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    fontSize: 13,
  },
  previewBox: {
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    padding: "14px 16px",
    fontSize: 13,
    whiteSpace: "pre-wrap",
    lineHeight: 1.7,
    color: "#e2e8f0",
    marginTop: 10,
    fontFamily: "monospace",
    maxHeight: 320,
    overflowY: "auto",
  },
  badge: (color) => ({
    background: color,
    color: "#fff",
    borderRadius: 20,
    padding: "2px 10px",
    fontSize: 11,
    fontWeight: 700,
  }),
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "#fff",
    marginBottom: 14,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  toast: (type) => ({
    position: "fixed",
    bottom: 20,
    left: "50%",
    transform: "translateX(-50%)",
    background: type === "error" ? "#dc2626" : "#16a34a",
    color: "#fff",
    borderRadius: 12,
    padding: "10px 22px",
    fontWeight: 600,
    fontSize: 14,
    zIndex: 9999,
    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
  }),
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function FBSchedulerPage() {
  const [config, setConfig]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [testing, setTesting]     = useState(false);
  const [logs, setLogs]           = useState([]);
  const [preview, setPreview]     = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [toast, setToast]         = useState(null);

  // Form state
  const [pageID, setPageID]           = useState("");
  const [pageToken, setPageToken]     = useState("");
  const [intervalHours, setIntervalHours] = useState(4);
  const [language, setLanguage]       = useState("both");
  const [enabled, setEnabled]         = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_API_URL}/fbscheduler`, { headers });
      if (!res.ok) throw new Error("Failed to load config");
      const data = await res.json();
      setConfig(data);
      setPageID(data.page_id || "");
      setPageToken(""); // don't prefill masked token
      setIntervalHours(data.interval_hours || 4);
      setLanguage(data.language || "both");
      setEnabled(data.enabled || false);
    } catch (e) {
      showToast("Could not load config: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_API_URL}/fbscheduler/logs?limit=15`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setLogs(data || []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchLogs();
  }, [fetchConfig, fetchLogs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        enabled,
        interval_hours: parseInt(intervalHours),
        language,
      };
      if (pageID.trim())    body.page_id    = pageID.trim();
      if (pageToken.trim()) body.page_token = pageToken.trim();

      const res = await fetch(`${BASE_API_URL}/fbscheduler`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Save failed");
      showToast(enabled ? "✅ Scheduler saved & started!" : "✅ Scheduler saved (disabled)");
      fetchConfig();
      fetchLogs();
    } catch (e) {
      showToast("Error: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTestNow = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${BASE_API_URL}/fbscheduler/testnow`, {
        method: "POST",
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Post failed");
      showToast(`📘 Posted! Post ID: ${data.post_id}`);
      fetchLogs();
    } catch (e) {
      showToast("Error: " + e.message, "error");
    } finally {
      setTesting(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await fetch(
        `${BASE_API_URL}/fbscheduler/preview?lang=${language}`,
        { headers }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Preview failed");
      setPreview(data);
    } catch (e) {
      showToast("Preview error: " + e.message, "error");
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚙️</div>
          <div style={{ color: "#94a3b8" }}>Loading Facebook Scheduler…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={S.title}>📘 Facebook Auto-Post Scheduler</div>
        <div style={S.sub}>
          Automatically post random VirtualKalvi questions to your Facebook Page every few hours.
        </div>

        {/* Status pill */}
        <div style={{
          display: "inline-flex", alignItems: "center",
          background: "rgba(255,255,255,0.08)",
          borderRadius: 20, padding: "6px 14px", fontSize: 13,
        }}>
          <span style={S.statusDot(config?.running)} />
          {config?.running ? "Scheduler is RUNNING" : "Scheduler is STOPPED"}
        </div>
      </div>

      {/* ── Config Card ── */}
      <div style={S.card}>
        <div style={S.sectionTitle}>⚙️ Configuration</div>

        <label style={S.label}>Facebook Page ID</label>
        <input
          style={{ ...S.input, opacity: 0.6, cursor: "not-allowed" }}
          value="61554849740185 (VirtualKalvi — pre-set)"
          readOnly
        />

        <label style={S.label}>
          Page Access Token{" "}
          {config?.page_token && (
            <span style={{ color: "#22c55e", fontWeight: 400, textTransform: "none" }}>
              (currently set: {config.page_token})
            </span>
          )}
        </label>
        <input
          style={S.input}
          type="password"
          placeholder="Paste new token to update (leave blank to keep existing)"
          value={pageToken}
          onChange={e => setPageToken(e.target.value)}
        />
        <div style={{ fontSize: 11, color: "#64748b", marginTop: -10, marginBottom: 14 }}>
          💡 Get from developers.facebook.com → Graph API Explorer → select your Page → Generate Token
        </div>

        <label style={S.label}>Post every (hours)</label>
        <select
          style={S.select}
          value={intervalHours}
          onChange={e => setIntervalHours(parseInt(e.target.value))}
        >
          <option value={1}>Every 1 hour</option>
          <option value={2}>Every 2 hours</option>
          <option value={3}>Every 3 hours</option>
          <option value={4}>Every 4 hours</option>
          <option value={6}>Every 6 hours</option>
          <option value={8}>Every 8 hours</option>
          <option value={12}>Every 12 hours</option>
          <option value={24}>Once a day</option>
        </select>

        <label style={S.label}>Question language</label>
        <select
          style={S.select}
          value={language}
          onChange={e => setLanguage(e.target.value)}
        >
          <option value="both">Tamil + English (both)</option>
          <option value="tamil">Tamil only</option>
          <option value="english">English only</option>
        </select>

        {/* Enable toggle */}
        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          background: "rgba(255,255,255,0.05)",
          borderRadius: 10, padding: "12px 16px", marginBottom: 16,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Auto-post enabled</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
              Turn on to start posting automatically at the set interval
            </div>
          </div>
          <div
            onClick={() => setEnabled(!enabled)}
            style={{
              width: 52, height: 28, borderRadius: 14,
              background: enabled ? "#16a34a" : "#475569",
              position: "relative", cursor: "pointer",
              transition: "background 0.2s",
            }}
          >
            <div style={{
              position: "absolute",
              top: 3, left: enabled ? 26 : 3,
              width: 22, height: 22,
              borderRadius: "50%", background: "#fff",
              transition: "left 0.2s",
            }} />
          </div>
        </div>

        <div style={S.row}>
          <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "💾 Save & Apply"}
          </button>
          <button style={S.btnGray} onClick={handlePreview} disabled={previewing}>
            {previewing ? "Loading…" : "👁 Preview Post"}
          </button>
          <button style={S.btnGreen} onClick={handleTestNow} disabled={testing}>
            {testing ? "Posting…" : "🚀 Post Now (Test)"}
          </button>
        </div>
      </div>

      {/* ── Preview ── */}
      {preview && (
        <div style={S.card}>
          <div style={S.sectionTitle}>
            👁 Post Preview
            <span style={S.badge("#2563eb")}>{preview.subject}</span>
            <span style={S.badge("#0d9488")}>{preview.grade}</span>
          </div>
          <div style={S.previewBox}>{preview.message}</div>
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button style={S.btnGray} onClick={handlePreview}>🔄 Another question</button>
            <button style={{ ...S.btnGray, opacity: 0.6 }} onClick={() => setPreview(null)}>✕ Close</button>
          </div>
        </div>
      )}

      {/* ── How to get token ── */}
      <div style={S.card}>
        <div style={S.sectionTitle}>🔑 How to get your Facebook Page Token</div>
        <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.9 }}>
          <div>1. Go to <a href="https://developers.facebook.com/tools/explorer" target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>developers.facebook.com/tools/explorer</a></div>
          <div>2. Select your <strong style={{ color: "#fff" }}>VirtualKalvi App</strong> and your <strong style={{ color: "#fff" }}>Facebook Page</strong></div>
          <div>3. Add permissions: <code style={{ background: "rgba(255,255,255,0.1)", padding: "1px 6px", borderRadius: 4 }}>pages_manage_posts</code> + <code style={{ background: "rgba(255,255,255,0.1)", padding: "1px 6px", borderRadius: 4 }}>pages_read_engagement</code></div>
          <div>4. Click <strong style={{ color: "#fff" }}>Generate Access Token</strong></div>
          <div>5. To make it long-lived (never expire), exchange it:</div>
          <div style={{
            background: "rgba(0,0,0,0.4)", borderRadius: 8,
            padding: "8px 12px", marginTop: 6, fontFamily: "monospace",
            fontSize: 11, color: "#a5f3fc", overflowX: "auto",
          }}>
            GET https://graph.facebook.com/v19.0/oauth/access_token?grant_type=fb_exchange_token&client_id=APP_ID&client_secret=APP_SECRET&fb_exchange_token=SHORT_TOKEN
          </div>
          <div style={{ marginTop: 6 }}>6. The token returned is your <strong style={{ color: "#22c55e" }}>long-lived Page Token</strong> — paste it above</div>
        </div>
      </div>

      {/* ── Recent Posts Log ── */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={S.sectionTitle}>📋 Recent Posts</div>
          <button style={{ ...S.btnGray, padding: "6px 12px", fontSize: 12 }} onClick={fetchLogs}>
            🔄 Refresh
          </button>
        </div>

        {logs.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: 13, textAlign: "center", padding: "20px 0" }}>
            No posts yet. Use "Post Now (Test)" to try it out.
          </div>
        ) : (
          logs.map((log, i) => (
            <div key={i} style={S.logRow}>
              <div>
                <span style={{ color: "#60a5fa", marginRight: 8 }}>📘</span>
                <span style={{ fontWeight: 600 }}>{log.subject_name}</span>
                <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: 12 }}>{log.grade_name}</span>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#64748b" }}>{log.posted_at}</div>
                <div style={{ fontSize: 10, color: "#475569" }}>{log.post_id}</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div style={S.toast(toast.type)}>{toast.msg}</div>
      )}
    </div>
  );
}
